import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Search, Loader2 } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

interface SearchBoxProps {
  onSearch: (statement: string, filters: SearchFilters) => void;
  isLoading: boolean;
}

export interface SearchFilters {
  yearFrom?: number;
  yearTo?: number;
  studyType?: string;
}

export const SearchBox = ({ onSearch, isLoading }: SearchBoxProps) => {
  const [statement, setStatement] = useState('');
  const [yearFrom, setYearFrom] = useState('');
  const [yearTo, setYearTo] = useState('');
  const [studyType, setStudyType] = useState('');
  const { t } = useLanguage();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const cleanStatement = statement.trim();
    if (cleanStatement) {
      onSearch(cleanStatement, {
        yearFrom: yearFrom ? parseInt(yearFrom) : undefined,
        yearTo: yearTo ? parseInt(yearTo) : undefined,
        studyType: studyType || undefined,
      });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e as any);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="w-full max-w-4xl mx-auto space-y-6 p-8 bg-background/60 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20">

      <div className="space-y-2">
        <Label htmlFor="statement" className="text-base font-medium">
          {t.enterStatement}
        </Label>
        <Textarea
          id="statement"
          value={statement}
          onChange={(e) => setStatement(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={t.examplePlaceholder}
          className="min-h-[120px] text-base resize-none"
          disabled={isLoading}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label htmlFor="yearFrom" className="text-sm">
            {t.yearFrom}
          </Label>
          <Input
            type="number"
            id="yearFrom"
            value={yearFrom}
            onChange={(e) => setYearFrom(e.target.value)}
            placeholder="2010"
            className="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            disabled={isLoading}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="yearTo" className="text-sm">
            {t.yearTo}
          </Label>
          <Input
            type="number"
            id="yearTo"
            value={yearTo}
            onChange={(e) => setYearTo(e.target.value)}
            placeholder="2024"
            className="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            disabled={isLoading}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="studyType" className="text-sm">
            {t.studyType}
          </Label>
          <select
            id="studyType"
            value={studyType}
            onChange={(e) => setStudyType(e.target.value)}
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            disabled={isLoading}
          >
            <option value="">{t.allTypes}</option>
            <option value="Clinical Trial">{t.clinicalTrial}</option>
            <option value="Review">{t.review}</option>
            <option value="Meta-Analysis">{t.metaAnalysis}</option>
            <option value="Systematic Review">{t.systematicReview}</option>
          </select>
        </div>
      </div>

      <Button
        type="submit"
        size="lg"
        className="w-full md:w-auto bg-primary hover:bg-primary/90 text-primary-foreground"
        disabled={isLoading || !statement.trim()}
      >
        {isLoading ? (
          <>
            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
            {t.searching}
          </>
        ) : (
          <>
            <Search className="mr-2 h-5 w-5" />
            {t.searchButton}
          </>
        )}
      </Button>
    </form>
  );
};
